import { t } from '../../i18n';
import type { AuthenticationFormResponse } from '../models/auth.model';
import { AuthenticationFormErrorCode, AuthenticationFormResponseType } from '../models/auth.model';

const ERROR_CODES = new Set([
  'login_required',
  'invalid_credentials',
  'account_already_exists',
  'invalid_email',
  'weak_password',
  'missing_fields',
  'invalid_name',
  'password_mismatch',
  'missing_email',
  'email_not_found',
  'email_verify',
  'captcha_challenge',
  'unknown_error',
]);

/** Looked up per call so the message follows the active locale, not the one at import time. */
const errorMessage = (code?: string): string =>
  t(`auth.error.${code && ERROR_CODES.has(code) ? code : 'unknown_error'}`);

class AuthenticationFormService {
  /**
   * Submit authentication form to Shopify endpoint
   * Handles CAPTCHA, error parsing, and success detection
   */
  async submitForm(
    actionUrl: string,
    redirectUrl: string,
    form: HTMLFormElement,
    params: URLSearchParams,
    callback: (response: any) => void
  ): Promise<AuthenticationFormResponse> {
    // Check if CAPTCHA is enabled
    if (this.isCaptchaEnabled()) {
      const captchaResponse = await this.handleCaptcha(form);
      params.set('h-captcha-response', captchaResponse);
    }

    const response = await this.submitFormInternal(actionUrl, redirectUrl, params);

    // Invoke callback with response
    callback(response);

    return response;
  }

  /**
   * Internal form submission with error handling
   */
  private async submitFormInternal(
    actionUrl: string,
    redirectUrl: string,
    params: URLSearchParams
  ): Promise<AuthenticationFormResponse> {
    try {
      const response = await fetch(actionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
        redirect: 'follow', // Follow redirects automatically
      });

      const responseText = await response.text();
      const responseUrl = response.url;

      // Check for HTML error messages
      const htmlError = this.extractErrorFromHTML(responseText);
      if (htmlError) {
        const errorCode =
          htmlError === 'email_verify'
            ? AuthenticationFormErrorCode.EMAIL_VERIFY
            : AuthenticationFormErrorCode.UNKNOWN_ERROR;

        return {
          type: AuthenticationFormResponseType.ERROR_CODE,
          response,
          redirectUrl: undefined,
          errorCode,
          errorMessage: this.convertToErrorMessage(errorCode, htmlError),
          error: undefined,
        };
      }

      // Check for error in URL query string
      if (responseUrl.includes('?error=')) {
        const errorParams = new URLSearchParams(responseUrl.split('?')[1]);
        const errorCodeString = errorParams.get('error') ?? '';
        const errorCode = this.convertToErrorCode(errorCodeString);

        return {
          type: AuthenticationFormResponseType.ERROR_CODE,
          response,
          redirectUrl: undefined,
          errorCode,
          errorMessage: this.convertToErrorMessage(errorCode, errorCodeString),
          error: undefined,
        };
      }

      // Check for successful redirect
      if (this.isSuccessfulRedirect(responseUrl, redirectUrl)) {
        return {
          type: AuthenticationFormResponseType.SUCCESS,
          response,
          redirectUrl: redirectUrl ?? responseUrl,
          errorCode: undefined,
          errorMessage: undefined,
          error: undefined,
        };
      }

      // Check for challenge redirect
      if (responseUrl.includes('/challenge')) {
        return {
          type: AuthenticationFormResponseType.ERROR_CODE,
          response,
          redirectUrl: responseUrl,
          errorCode: AuthenticationFormErrorCode.CAPTCHA_CHALLENGE,
          errorMessage: errorMessage('captcha_challenge'),
          error: undefined,
        };
      }

      // Check for a password-reset success message embedded in the HTML
      // (Shopify redirects to /account/login after recovery, so isSuccessfulRedirect returns false)
      const resetSuccessMessage = this.extractPasswordResetSuccessFromHTML(responseText);
      if (resetSuccessMessage) {
        return {
          type: AuthenticationFormResponseType.SUCCESS,
          response,
          redirectUrl: undefined,
          successMessage: resetSuccessMessage,
          errorCode: undefined,
          errorMessage: undefined,
          error: undefined,
        };
      }

      // Unknown response
      return {
        type: AuthenticationFormResponseType.ERROR_CODE,
        response,
        redirectUrl: undefined,
        errorCode: AuthenticationFormErrorCode.UNKNOWN_ERROR,
        errorMessage: errorMessage('unknown_error'),
        error: undefined,
      };
    } catch (error: any) {
      return {
        type: AuthenticationFormResponseType.ERROR_CODE,
        response: undefined,
        redirectUrl: undefined,
        errorCode: AuthenticationFormErrorCode.UNKNOWN_ERROR,
        errorMessage: errorMessage('unknown_error'),
        error,
      };
    }
  }

  /**
   * Handle CAPTCHA challenge
   * Returns hCaptcha response token
   */
  private async handleCaptcha(form: HTMLFormElement): Promise<string> {
    return new Promise((resolve) => {
      const formClone = document.createElement('form');
      formClone.innerHTML = form.innerHTML;

      // Prevent form from actually navigating on submit
      formClone.action = 'javascript:void(0)';
      formClone.method = 'post';

      // Append to DOM (required for form submission)
      formClone.style.display = 'none';
      document.body.appendChild(formClone);

      // Prevent actual navigation when form submits
      formClone.addEventListener('submit', (e) => {
        e.preventDefault();
        return false;
      });

      (window as any).Shopify.captcha.protect(formClone, () => {
        formClone.submit();

        // Poll for CAPTCHA response
        this.pollForResult(
          100,
          () => {
            const responseElement = formClone.querySelector(
              '[data-hcaptcha-response]'
            ) as HTMLElement;
            return !!responseElement?.getAttribute('data-hcaptcha-response');
          },
          () => {
            const responseElement = formClone.querySelector(
              '[data-hcaptcha-response]'
            ) as HTMLElement;
            const captchaResponse = responseElement?.getAttribute('data-hcaptcha-response') ?? '';

            // Clean up - remove clone from DOM
            document.body.removeChild(formClone);

            resolve(captchaResponse);
          }
        );
      });
    });
  }

  /**
   * Extract error message from HTML response
   */
  private extractErrorFromHTML(html: string): string | null {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Dawn / 2.0 themes: <p class="form__message form__message--error">...</p>
    const dawn = doc.querySelector('.form__message--error');
    if (dawn) {
      const msg = dawn.innerHTML?.trim();
      if (msg) {
        if (dawn.textContent?.includes('verify your email address')) return 'email_verify';
        return this.decodeHTMLEntities(msg);
      }
    }

    // Legacy themes: <h2 class="form__message"> or <div class="errors"><ul><li>...
    const formMessage = doc.querySelector('h2.form__message') || doc.querySelector('div.errors');
    const errorList =
      formMessage?.querySelector('li') || formMessage?.nextElementSibling?.querySelector('li');

    if (errorList) {
      const msg = errorList.innerHTML?.trim();
      if (errorList.textContent?.includes('verify your email address')) {
        return 'email_verify';
      }
      return this.decodeHTMLEntities(msg) ?? null;
    }

    return null;
  }

  /**
   * Extract success message from HTML response for password recovery.
   * Shopify redirects to /account/login after a successful /account/recover POST,
   * so we can't rely on the URL — instead we look for the success notice in the HTML.
   * Handles Dawn (.form__message without --error), themes with .form__message--success,
   * and themes using [data-notification-text].
   */
  private extractPasswordResetSuccessFromHTML(html: string): string | null {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // If there's an error message on the page, don't treat it as success
    if (doc.querySelector('.form__message--error')) return null;

    // Explicit success class (any theme)
    const successEl = doc.querySelector('.form__message--success');
    if (successEl) {
      const msg = successEl.innerHTML?.trim();
      if (msg) return this.decodeHTMLEntities(msg);
    }

    // Dawn / 2.0 themes: form__message shown when recover.attempted
    const formMsgEl = doc.querySelector('.form__message');
    if (formMsgEl) {
      const msg = formMsgEl.innerHTML?.trim();
      if (msg) return this.decodeHTMLEntities(msg);
    }

    return null;
  }

  /**
   * Decode HTML entities (e.g. &lt; → <) so the result can be safely
   * passed to dangerouslySetInnerHTML and render as real HTML.
   */
  private decodeHTMLEntities(str: string): string {
    const el = document.createElement('textarea');
    el.innerHTML = str;
    return el.value;
  }

  /**
   * Poll for a condition to be true, then call callback
   */
  private pollForResult(period: number, check: () => boolean, callback: () => void): void {
    const interval = setInterval(() => {
      if (check()) {
        clearInterval(interval);
        callback();
      }
    }, period);
  }

  /**
   * Check if response URL indicates successful authentication
   */
  private isSuccessfulRedirect(responseUrl: string, redirectUrl: string): boolean {
    if (!responseUrl) return false;

    // Staying on /account/login means authentication failed
    try {
      if (new URL(responseUrl).pathname === '/account/login') return false;
    } catch {}

    if (responseUrl === redirectUrl || responseUrl.includes('/account')) return true;

    try {
      const responseURL = new URL(responseUrl);
      const redirectURL = new URL(redirectUrl);
      return responseURL.pathname === '/' || responseURL.pathname === redirectURL.pathname;
    } catch {
      return false;
    }
  }

  /**
   * Check if Shopify CAPTCHA is available
   */
  private isCaptchaEnabled(): boolean {
    return !!(window as any).Shopify?.captcha;
  }

  /**
   * Convert string to error code enum
   */
  private convertToErrorCode(code: string): AuthenticationFormErrorCode {
    const enumKey = code.toUpperCase();
    if (enumKey in AuthenticationFormErrorCode) {
      return AuthenticationFormErrorCode[enumKey as keyof typeof AuthenticationFormErrorCode];
    }
    return AuthenticationFormErrorCode.UNKNOWN_ERROR;
  }

  /**
   * Get user-friendly error message
   */
  private convertToErrorMessage(
    code: AuthenticationFormErrorCode,
    fallbackMessage?: string
  ): string {
    if (fallbackMessage && (!code || code === AuthenticationFormErrorCode.UNKNOWN_ERROR)) {
      return fallbackMessage;
    }
    return errorMessage(code);
  }
}

export const authenticationFormService = new AuthenticationFormService();
